import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { FORMAZIONE, FORMAZIONE_NOTE } from "../data/content";
import { ArrowRight, GraduationCap } from "lucide-react";

const TimelineItem = ({ item, index }) => (
  <motion.div
    className="relative pl-12 md:pl-0 md:grid md:grid-cols-12 md:gap-8 pb-14 last:pb-0"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`formazione-item-${index}`}
  >
    <span className="absolute left-[-6px] md:left-1/4 md:-translate-x-1/2 top-2 w-3.5 h-3.5 rounded-full bg-sage ring-4 ring-cream" />
    <div className="md:col-span-3 md:text-right md:pr-10">
      <span className="font-serif text-3xl md:text-4xl text-sage-dark">{item.year}</span>
    </div>
    <div className="md:col-span-9 mt-2 md:mt-0">
      <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug">{item.title}</h3>
      <p className="text-stone leading-relaxed mt-3 max-w-2xl">{item.place}</p>
    </div>
  </motion.div>
);

export default function Formazione() {
  useSeo(
    "Formazione — Susanna Paoletti, Psicoterapeuta a Macerata",
    "La formazione di Susanna Paoletti: laurea in Psicologia, specializzazione in psicoterapia cognitivo-comportamentale, sessuologia clinica ed EMDR."
  );

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <>
      <PageHeader
        eyebrow="Formazione"
        title="Titoli e specializzazioni"
        subtitle="Le tappe del mio percorso formativo, dalla laurea alle specializzazioni in psicoterapia, sessuologia clinica ed EMDR."
      />

      <section className="py-20 md:py-28" data-testid="formazione-timeline">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-sage-dark mb-14">
            <GraduationCap size={22} />
            <span className="text-xs uppercase tracking-[0.28em]">Percorso formativo</span>
          </div>

          <div ref={ref} className="relative">
            <div className="absolute top-0 bottom-0 left-[1px] md:left-1/4 md:-translate-x-1/2 w-px bg-border" />
            <motion.div
              className="absolute top-0 left-[1px] md:left-1/4 md:-translate-x-1/2 w-px bg-sage origin-top"
              style={{ scaleY, height: "100%" }}
            />
            <div className="relative">
              {FORMAZIONE.map((item, i) => (
                <TimelineItem key={i} item={item} index={i} />
              ))}
            </div>
          </div>

          <Reveal>
            <div className="mt-8 md:mt-14 bg-sand border border-border rounded-2xl p-10 md:p-14">
              <GraduationCap size={28} className="text-sage-dark mb-5" />
              <p className="font-serif italic text-2xl md:text-4xl text-ink leading-snug tracking-tight max-w-4xl">
                {FORMAZIONE_NOTE}
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-14 flex flex-col sm:flex-row gap-4">
              <PillButton to="/esperienze" testid="formazione-esperienze">
                Vedi le esperienze professionali <ArrowRight size={18} />
              </PillButton>
              <PillButton to="/contatti" variant="outline" testid="formazione-cta">
                Richiedi un primo colloquio
              </PillButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
